import React, { useState } from 'react'
import { Avatar, Modal, Spin } from 'antd'
import {
  CheckCircle,
  XCircle,
  PencilSimpleLine,
  User,
  Trash,
} from '@phosphor-icons/react'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { getNameSignature } from 'helper/utility'
import { axiosGet } from 'Redux/axiosHelper'
import { successMessage,failureMessage } from 'api/global'
import { useRemoveFromLeague } from 'api/league/LeagueAttendance/leagueAttendance'
import PlayerProfileModal from 'pages/Events/League/Modals/PlayerProfileModal'
import { LeaguePlayerParticipant } from 'types/league/leagueManage'
import closeIcon from 'assets/Images/close.svg'

const PROFILE_ENDPOINT: Record<string, string> = {
  kid: 'v1/kids',
  guardian: 'v1/guardians',
  authorizedAdult: 'v1/authorized-adults',
}

const ManageAttendeeModal: React.FC = () => {
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileData, setProfileData] = useState<LeaguePlayerParticipant | null>(null)

  const {
    manageAttendeeModal,
    setManageAttendeeModal,
    selectedAttendee,
    setCheckInModal,
    setCheckOutModal,
    setDropInDetailModalOpen,
    setDropInMode,
  } = leagueAttendanceStore(store => ({
    manageAttendeeModal: store.manageAttendeeModal,
    setManageAttendeeModal: store.setManageAttendeeModal,
    selectedAttendee: store.selectedAttendee,
    setCheckInModal: store.setCheckInModal,
    setCheckOutModal: store.setCheckOutModal,
    setDropInDetailModalOpen: store.setDropInDetailModalOpen,
    setDropInMode: store.setDropInMode,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const { mutateAsync: removeFromLeague, isLoading: removing } = useRemoveFromLeague()

  const closeModal = () => {
    setManageAttendeeModal(false)
    setProfileData(null)
  }

  const handleCheckIn = () => {
    setManageAttendeeModal(false)
    setCheckInModal(true)
  }

  const handleCheckOut = () => {
    setManageAttendeeModal(false)
    setCheckOutModal(true)
  }

  const handleDropInCheckIn = () => {
    setManageAttendeeModal(false)
    setDropInMode('check-in')
    setDropInDetailModalOpen(true)
  }

  const handleDropInCheckOut = () => {
    setManageAttendeeModal(false)
    setDropInMode('check-out')
    setDropInDetailModalOpen(true)
  }

  const handleViewProfile = async () => {
    if (!selectedAttendee) return
    const { participantType, _id } = selectedAttendee

    if (participantType === 'dropInAttendee') {
      failureMessage('Profile not available for drop-in attendees')
      return
    }

    const endpoint = PROFILE_ENDPOINT[participantType || 'kid']
    if (!endpoint) return

    setProfileLoading(true)
    try {
      const res = await axiosGet(`${endpoint}/${_id}`, {})
      setProfileData(res.data)
    } catch {
      failureMessage('Could not load profile')
    } finally {
      setProfileLoading(false)
    }
  }

  const handleRemove = () => {
    if (!selectedAttendee || !leagueData?._id) return

    Modal.confirm({
      title: 'Remove from league?',
      content: `This will cancel all registrations for ${selectedAttendee.name} in this league. This cannot be undone.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        const { participantType, _id } = selectedAttendee
        const idField =
          participantType === 'kid' ? { kidId: _id }
          : participantType === 'guardian' ? { guardianId: _id }
          : participantType === 'dropInAttendee' ? { dropInAttendeeId: _id }
          : { authorizedAdultId: _id }

        try {
          await removeFromLeague({ leagueId: leagueData._id, ...idField })
          successMessage('Removed from league')
          closeModal()
        } catch {
          failureMessage('Failed to remove — please try again')
        }
      },
    })
  }

  if (!selectedAttendee) return null

  const isDropIn = selectedAttendee.participantType === 'dropInAttendee'

  return (
    <>
      <Modal
        open={manageAttendeeModal}
        onCancel={closeModal}
        footer={null}
        width={520}
        centered
        maskClosable={false}
        className="custom-common-modal att-manage-modal"
      >
        <div className="px-15px modal-body">
          <div className="flex-center pt-20px justify-between">
            <h2 className="w-600 text-24">Manage Attendee</h2>
            <img
              src={closeIcon}
              alt="close"
              onClick={closeModal}
              className="close pointer"
            />
          </div>

          <div className="mt-30px modal-content">
            {/* Player info */}
            <div className="att-modal-player-info">
              <Avatar size={56} className="att-modal-avatar">
                {getNameSignature(selectedAttendee.name)}
              </Avatar>
              <div className="att-modal-player-details">
                <span className="att-modal-player-name">
                  {selectedAttendee.name}
                </span>
                <span className="att-modal-player-meta">
                  {selectedAttendee.organization} &middot;{' '}
                  {selectedAttendee.team} &middot; {selectedAttendee.division}
                </span>
              </div>
            </div>

            {/* Action options */}
            <div className="att-modal-actions-list">
              {isDropIn ? (
                <>
                  <button className="att-modal-action-btn" onClick={handleDropInCheckIn}>
                    <CheckCircle size={20} color="#2ba7ca" />
                    <span>Check In</span>
                  </button>
                  <button className="att-modal-action-btn" onClick={handleDropInCheckOut}>
                    <XCircle size={20} color="#000" />
                    <span>Check Out</span>
                  </button>
                </>
              ) : (
                <>
                  <button className="att-modal-action-btn" onClick={handleCheckIn}>
                    <CheckCircle size={20} color="#2ba7ca" />
                    <span>Check In</span>
                  </button>
                  <button className="att-modal-action-btn" onClick={handleCheckOut}>
                    <XCircle size={20} color="#000" />
                    <span>Check Out</span>
                  </button>
                </>
              )}

              <button
                className="att-modal-action-btn att-action-disabled"
                disabled
                title="Edit registration — coming soon"
              >
                <PencilSimpleLine size={20} color="#bbb" />
                <span style={{ color: '#bbb' }}>Edit Registration</span>
              </button>

              {!isDropIn && (
                <button
                  className="att-modal-action-btn"
                  onClick={handleViewProfile}
                  disabled={profileLoading}
                >
                  {profileLoading
                    ? <Spin size="small" />
                    : <User size={20} color="#000" />
                  }
                  <span>View Profile</span>
                </button>
              )}

              <button
                className="att-modal-action-btn att-action-danger"
                onClick={handleRemove}
                disabled={removing}
              >
                <Trash size={20} color="#ff6961" />
                <span>Remove from league</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {profileData && (
        <PlayerProfileModal
          player={profileData}
          participantType={
            selectedAttendee.participantType === 'dropInAttendee'
              ? 'kid'
              : (selectedAttendee.participantType as any) || 'kid'
          }
          onClose={() => setProfileData(null)}
        />
      )}
    </>
  )
}

export default ManageAttendeeModal
